import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import React from 'react';

import {ScenePreview, ScenePreviewSource} from './ScenePreview';
import cs from './styles.module.scss';

export type {ScenePreviewSource} from './ScenePreview';

/**
 * The square where one scene's preview is drawn. Its size is fixed so the surrounding layout
 * doesn't jump as scenes of different shapes go by.
 */
export const ScenePreviewStage: React.FC<{
  /** Null shows the fallback instead: the feature or scene isn't loaded. */
  source: ScenePreviewSource | null;
  fallback?: React.ReactNode;
  /** Width and height in px. */
  size: number;
  blockedSources?: string[];
  isFeatureHidden?: boolean;
  className?: string;
}> = ({source, fallback, size, blockedSources, isFeatureHidden, className}) => (
  <div className={classnames(cs.stage, className)} style={{width: size, height: size}}>
    {source ? (
      <ScenePreview
        source={source}
        size={size}
        blockedSources={blockedSources}
        isFeatureHidden={isFeatureHidden}
      />
    ) : (
      fallback
    )}
  </div>
);

/** Prev, a "3 of 12" counter, and Next. Renders nothing when there is only one scene. */
export const ScenePreviewControls: React.FC<{
  count: number;
  index: number;
  onIndexChange: (index: number) => void;
  className?: string;
}> = ({count, index, onIndexChange, className}) => {
  if (count <= 1) {
    return null;
  }
  return (
    <div className={classnames(cs.controls, className)}>
      <B.Button
        minimal
        icon="chevron-left"
        text="Prev"
        disabled={index <= 0}
        onClick={() => onIndexChange(index - 1)}
      />
      <span className={cs.counter} aria-live="polite">
        {index + 1} of {count}
      </span>
      <B.Button
        minimal
        rightIcon="chevron-right"
        text="Next"
        disabled={index >= count - 1}
        onClick={() => onIndexChange(index + 1)}
      />
    </div>
  );
};
